import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronRight, Loader2, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import { AdminGuard } from '../components/guards';
import { SchoolMatchReview } from '../components/imports';
import { useImportStore } from '../store/importStore';
import type { MatchReviewStatus } from '../types/import';

const TABS: { key: MatchReviewStatus | 'all'; label: string; countKey?: keyof NonNullable<ReturnType<typeof useImportStore.getState>['matchCounts']> }[] = [
  { key: 'PENDING_REVIEW', label: 'Pending Review', countKey: 'pendingReview' },
  { key: 'AUTO_MATCHED', label: 'Auto-Matched', countKey: 'autoMatched' },
  { key: 'CONFIRMED', label: 'Confirmed', countKey: 'confirmed' },
  { key: 'CREATE_NEW', label: 'Create New', countKey: 'createNew' },
  { key: 'SKIPPED', label: 'Skipped', countKey: 'skipped' },
  { key: 'all', label: 'All' },
];

/**
 * Import review page — review school matches before executing import
 */
export const ImportReview = () => {
  const { batchId } = useParams<{ batchId: string }>();
  const navigate = useNavigate();
  const {
    currentBatch,
    schoolMatches,
    matchCounts,
    matchFilter,
    matchSort,
    matchPagination,
    isLoading,
    error,
    clearError,
    fetchBatchDetail,
    fetchSchoolMatches,
    reviewMatch,
    executeImport,
    bulkReview,
    pollBatchStatus,
    setMatchFilter,
    setMatchSort,
    setMatchPage,
  } = useImportStore();

  const [isExecuting, setIsExecuting] = useState(false);
  const [isBulkProcessing, setIsBulkProcessing] = useState(false);

  useEffect(() => {
    if (batchId) {
      fetchBatchDetail(batchId);
      fetchSchoolMatches(batchId, matchFilter);
    }
  }, [batchId, fetchBatchDetail, fetchSchoolMatches, matchFilter]);

  const handleReview = async (matchId: string, status: string, orgId?: string) => {
    if (!batchId) return;
    try {
      await reviewMatch(batchId, matchId, status, orgId);
      toast.success('Match updated');
    } catch {
      toast.error('Failed to update match');
    }
  };

  const handleExecuteImport = async () => {
    if (!batchId) return;

    const pending = matchCounts?.pendingReview || 0;
    if (pending > 0) {
      toast.error(`${pending} schools still pending review`);
      return;
    }

    const skipped = matchCounts?.skipped || 0;
    if (skipped > 0) {
      const confirmed = window.confirm(
        `${skipped} school(s) will be skipped. Continue with import?`
      );
      if (!confirmed) return;
    }

    try {
      setIsExecuting(true);
      await executeImport(batchId);
      toast.success('Import started — processing...');
      // Poll until import completes
      const result = await pollBatchStatus(batchId);
      if (result.status === 'COMPLETED') {
        toast.success('Import complete!');
      } else if (result.status === 'FAILED') {
        toast.error('Import failed. Check summary for details.');
      }
      navigate(`/admin/imports/${batchId}/summary`);
    } catch {
      toast.error('Failed to start import');
    } finally {
      setIsExecuting(false);
    }
  };

  const totalReviewed = matchCounts
    ? matchCounts.confirmed + matchCounts.createNew + matchCounts.skipped + matchCounts.autoMatched
    : 0;
  const totalMatches = matchCounts
    ? totalReviewed + (matchCounts.pendingReview || 0)
    : 0;

  const totalPages = Math.ceil(matchPagination.total / matchPagination.limit);

  return (
    <AdminGuard>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/admin/imports" className="hover:text-gray-700 dark:hover:text-gray-300">
              Data Imports
            </Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-gray-700 dark:text-gray-300">
              {currentBatch ? 'Batch' : '...'}
            </span>
            <ChevronRight className="h-4 w-4" />
            <span className="text-gray-900 dark:text-gray-100 font-medium">Review</span>
          </nav>

          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                Review School Matches
              </h1>
              {totalMatches > 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {totalReviewed} of {totalMatches} schools reviewed
                </p>
              )}
            </div>
            <button
              onClick={handleExecuteImport}
              disabled={isExecuting || (matchCounts?.pendingReview || 0) > 0}
              className="px-4 py-2 text-sm font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
            >
              {isExecuting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Play className="h-4 w-4 mr-2" />
                  Run Import
                </>
              )}
            </button>
          </div>

          {/* Import progress banner */}
          {isExecuting && currentBatch && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <Loader2 className="h-5 w-5 animate-spin text-blue-600 dark:text-blue-400" />
                <span className="font-medium text-blue-800 dark:text-blue-300">
                  {currentBatch.status === 'IMPORTING' ? 'Importing people...' : 'Starting import...'}
                </span>
                {currentBatch.stats && currentBatch.stats.importTotal > 0 && (
                  <span className="text-sm text-blue-600 dark:text-blue-400">
                    {currentBatch.stats.importProgress || 0} / {currentBatch.stats.importTotal} records
                  </span>
                )}
              </div>
              {currentBatch.stats && currentBatch.stats.importTotal > 0 && (
                <div className="w-full bg-blue-200 dark:bg-blue-800 rounded-full h-3">
                  <div
                    className="bg-blue-500 h-3 rounded-full transition-all duration-500"
                    style={{
                      width: `${Math.round(((currentBatch.stats.importProgress || 0) / currentBatch.stats.importTotal) * 100)}%`,
                    }}
                  />
                </div>
              )}
              {currentBatch.stats && (currentBatch.stats.personsCreated > 0 || currentBatch.stats.personsUpdated > 0) && (
                <div className="flex space-x-4 mt-2 text-xs text-blue-600 dark:text-blue-400">
                  <span>{currentBatch.stats.personsCreated || 0} created</span>
                  <span>{currentBatch.stats.personsUpdated || 0} updated</span>
                  <span>{currentBatch.stats.errors || 0} errors</span>
                </div>
              )}
            </div>
          )}

          {/* Error banner */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center justify-between">
              <span className="text-red-800 dark:text-red-300">{error}</span>
              <button
                onClick={clearError}
                className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Bulk actions */}
          {(matchCounts?.pendingReview || 0) > 0 && (
            <div className="flex items-center space-x-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <span className="text-sm text-gray-600 dark:text-gray-400">Bulk actions:</span>
              <button
                onClick={async () => {
                  if (!batchId) return;
                  const confirmed = window.confirm(
                    'Confirm top-ranked candidate for all pending matches with 100% score?'
                  );
                  if (!confirmed) return;
                  setIsBulkProcessing(true);
                  try {
                    const count = await bulkReview(batchId, 'confirm_top', 100);
                    toast.success(`Confirmed ${count} matches`);
                  } catch { toast.error('Bulk confirm failed'); }
                  setIsBulkProcessing(false);
                }}
                disabled={isBulkProcessing || isLoading}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-700 text-white hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isBulkProcessing ? 'Processing...' : `Confirm All 100% Matches`}
              </button>
              <button
                onClick={async () => {
                  if (!batchId) return;
                  const confirmed = window.confirm(
                    'Confirm top-ranked candidate for all pending matches with score >= 80%?'
                  );
                  if (!confirmed) return;
                  setIsBulkProcessing(true);
                  try {
                    const count = await bulkReview(batchId, 'confirm_top', 80);
                    toast.success(`Confirmed ${count} matches`);
                  } catch { toast.error('Bulk confirm failed'); }
                  setIsBulkProcessing(false);
                }}
                disabled={isBulkProcessing || isLoading}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isBulkProcessing ? 'Processing...' : `Confirm All 80%+ Matches`}
              </button>
              <button
                onClick={async () => {
                  if (!batchId) return;
                  const confirmed = window.confirm(
                    'Create new schools for all unmatched entries (no candidates found)?'
                  );
                  if (!confirmed) return;
                  setIsBulkProcessing(true);
                  try {
                    const count = await bulkReview(batchId, 'create_unmatched');
                    toast.success(`Marked ${count} schools as Create New`);
                  } catch { toast.error('Bulk create failed'); }
                  setIsBulkProcessing(false);
                }}
                disabled={isBulkProcessing || isLoading}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isBulkProcessing ? 'Processing...' : `Create All Unmatched Schools`}
              </button>
              <button
                onClick={async () => {
                  if (!batchId) return;
                  const pending = matchCounts?.pendingReview || 0;
                  const confirmed = window.confirm(
                    `Skip all ${pending} remaining pending reviews? These schools will not be imported.`
                  );
                  if (!confirmed) return;
                  setIsBulkProcessing(true);
                  try {
                    const count = await bulkReview(batchId, 'skip_all_pending');
                    toast.success(`Skipped ${count} matches`);
                  } catch { toast.error('Bulk skip failed'); }
                  setIsBulkProcessing(false);
                }}
                disabled={isBulkProcessing || isLoading}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-500 text-white hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isBulkProcessing ? 'Processing...' : `Skip All Pending Review`}
              </button>
            </div>
          )}

          {/* Status tabs + sort */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
            <nav className="flex -mb-px space-x-6 overflow-x-auto">
              {TABS.map((tab) => {
                const count = tab.countKey && matchCounts ? matchCounts[tab.countKey] : null;
                const isActive = matchFilter === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setMatchFilter(tab.key)}
                    className={`py-2 px-1 border-b-2 text-sm font-medium whitespace-nowrap ${
                      isActive
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    {tab.label}
                    {count != null && (
                      <span
                        className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                          isActive
                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                        }`}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
            <div className="flex items-center space-x-2 pb-2">
              <label className="text-xs text-gray-500 dark:text-gray-400">Sort:</label>
              <select
                value={matchSort}
                onChange={(e) => setMatchSort(e.target.value)}
                className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
              >
                <option value="name">Name A-Z</option>
                <option value="confidence">Match Quality</option>
                <option value="state">State</option>
              </select>
            </div>
            </div>
          </div>

          {/* Match cards */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              <span className="ml-2 text-gray-500 dark:text-gray-400">Loading matches...</span>
            </div>
          ) : schoolMatches.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No matches in this category.
            </div>
          ) : (
            <div className="space-y-4">
              {schoolMatches.map((match) => (
                <SchoolMatchReview key={match.id} match={match} onReview={handleReview} />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Page {matchPagination.page} of {totalPages}
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setMatchPage(matchPagination.page - 1)}
                  disabled={matchPagination.page <= 1}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setMatchPage(matchPagination.page + 1)}
                  disabled={matchPagination.page >= totalPages}
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminGuard>
  );
};
